import { CircularProgress } from "@mui/material";
import {
    useAttachedChatbotDocumentsQuery,
    useRemoveChatbotDocumentMutation,
} from "ApiLayer/Chatbot/queryChatbotDocuments";
import { DownCarrot } from "components/atoms/Icon";
import { useState } from "react";
import { getFileTypeBadge } from "utils/helpers";
import { useCollapsible } from "../shared/useCollapsible";
import styles from "./AttachedDocuments.module.scss";

interface AttachedDocumentsProps {
    buildingModelUid: string | null;
    onAddDocument?: () => void;
}

function AttachedDocuments({ buildingModelUid, onAddDocument }: AttachedDocumentsProps) {
    const { data: attachedDocuments, isLoading } = useAttachedChatbotDocumentsQuery(buildingModelUid);
    const { mutate: removeDocument } = useRemoveChatbotDocumentMutation();
    const { isExpanded, handleToggle, handleKeyDown, setIsExpanded } = useCollapsible();
    const [removingUid, setRemovingUid] = useState<string | null>(null);

    if (!buildingModelUid || isLoading || !attachedDocuments || attachedDocuments.length === 0) {
        return null;
    }

    const getFileTypeIcon = (mimeType: string) => {
        const { label, variant } = getFileTypeBadge(mimeType);
        const variantClassMap: Record<string, string> = {
            pdf: "",
            html: styles.fileBadgeDefault,
            xls: styles.fileBadgeExcel,
            doc: styles.fileBadgeWord,
            ppt: styles.fileBadgeDefault,
            txt: styles.fileBadgeDefault,
            csv: styles.fileBadgeCsv,
            json: styles.fileBadgeJson,
            file: styles.fileBadgeDefault,
        };
        const variantClass = variantClassMap[variant] ?? styles.fileBadgeDefault;
        return <span className={`${styles.fileBadge} ${variantClass}`}>{label}</span>;
    };

    const formatDate = (dateString: string) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return "";
        }
    };

    const handleRemove = (uploadUid: string, e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (buildingModelUid) {
            setRemovingUid(uploadUid);
            removeDocument(
                {
                    buildingModelUid,
                    uploadUid,
                },
                {
                    onSuccess: () => {
                        setRemovingUid(null);
                        if (attachedDocuments.length === 1) {
                            setIsExpanded(false);
                        }
                    },
                    onError: () => {
                        setRemovingUid(null);
                    },
                }
            );
        }
    };

    if (!isExpanded) {
        return (
            <div className={styles.collapsedBadge}>
                <button
                    type="button"
                    onClick={handleToggle}
                    onKeyDown={handleKeyDown}
                    className={styles.toggleButton}
                    aria-expanded={false}
                    aria-label={`${attachedDocuments.length} attached document${attachedDocuments.length !== 1 ? "s" : ""}. Click to expand`}
                >
                    <span className={styles.badgeText}>
                        {attachedDocuments.length} {attachedDocuments.length === 1 ? "document" : "documents"} attached
                    </span>
                    <span className={`${styles.chevronIcon} ${styles.chevronDown}`}>
                        <DownCarrot />
                    </span>
                </button>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleGroup}>
                    <span className={styles.title}>Attached Documents</span>
                    <span className={styles.count}>({attachedDocuments.length})</span>
                </div>
                <button
                    type="button"
                    onClick={handleToggle}
                    onKeyDown={handleKeyDown}
                    className={styles.collapseButton}
                    aria-expanded={true}
                    aria-label="Collapse attached documents"
                    title="Collapse"
                >
                    <span className={`${styles.chevronIcon} ${styles.chevronUp}`}>
                        <DownCarrot />
                    </span>
                </button>
            </div>
            <div className={styles.documentList}>
                {attachedDocuments.map((doc) => (
                    <div key={doc.upload_uid} className={styles.documentItem}>
                        <span className={styles.fileIcon}>{getFileTypeIcon(doc.mime_type)}</span>
                        <div className={styles.documentInfo}>
                            <div className={styles.fileName} title={doc.file_name}>
                                {doc.file_name}
                            </div>
                            <div className={styles.addedAt}>Added {formatDate(doc.added_at)}</div>
                        </div>
                        <button
                            type="button"
                            onClick={(e) => handleRemove(doc.upload_uid, e)}
                            disabled={removingUid !== null}
                            className={styles.removeButton}
                            title="Remove document"
                            aria-label={`Remove ${doc.file_name}`}
                        >
                            {removingUid === doc.upload_uid ? (
                                <CircularProgress size={16} style={{ color: "#6b7280" }} />
                            ) : (
                                "×"
                            )}
                        </button>
                    </div>
                ))}
            </div>
            {onAddDocument && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onAddDocument();
                    }}
                    disabled={removingUid !== null}
                    className={styles.addButton}
                    aria-label="Attach documents"
                    title="Attach documents"
                >
                    Attach documents
                </button>
            )}
        </div>
    );
}

export default AttachedDocuments;
